import React, { useState, useEffect, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
  Calendar,
  Popover,
} from '@c2fo/liquidity';
import { CalendarIcon } from '@c2fo/liquidity/icons';
import { format, subHours } from 'date-fns';
import { LoadingOverlay } from '../LoadingSpinner';
import { ListViewSwitch } from '../ListViewSwitch';
import { DeliveryTable } from '../DeliveryTable';
import { DeliveryGrid } from '../DeliveryGrid';
import { useDeliveriesState, useApiContext } from '../../contexts';
import type { DeliveriesSortField } from '../../contexts/FileDeliveryContext';
import { SavedSearches } from '../SavedSearches';
import { useSavedSearches, type SavedSearch } from '../../hooks';
import { Pagination } from '../Pagination';
import { PageHeader } from '../PageHeader';

const FILTER_FIELDS = [
  { value: 'state', label: 'Status', type: 'select' as const },
  { value: 'end_time', label: 'End Time', type: 'date' as const },
];

const STATE_OPTIONS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'RUNNING', label: 'Running' },
  { value: 'SUCCEEDED', label: 'Succeeded' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const DATE_OPERATORS = [
  { value: '>', label: 'After' },
  { value: '<', label: 'Before' },
  { value: '=', label: 'Equals' },
  { value: 'between', label: 'Between' },
];

const SORT_OPTIONS = [
  { value: 'end_time:desc', label: 'End Time (Newest)' },
  { value: 'end_time:asc', label: 'End Time (Oldest)' },
  { value: 'state:asc', label: 'Status (A-Z)' },
  { value: 'state:desc', label: 'Status (Z-A)' },
];

const createDefaultDeliveriesSearch = (): SavedSearch => {
  const twentyFourHoursAgo = subHours(new Date(), 24);
  const formattedDate = format(twentyFourHoursAgo, "yyyy-MM-dd'T'HH:mm:ss'Z'");
  return {
    name: 'Default',
    filter: `end_time > "${formattedDate}"`,
    sortField: 'end_time',
    sortDirection: 'desc',
    isDefault: true,
  };
};

export const DeliveriesListView: React.FC = () => {
  const [filterField, setFilterField] = useState('state');
  const [filterValue, setFilterValue] = useState('');
  const [dateOperator, setDateOperator] = useState('>');
  const [dateValue, setDateValue] = useState<Date | undefined>(undefined);
  const [dateValueEnd, setDateValueEnd] = useState<Date | undefined>(undefined);
  const hasFetchedRef = React.useRef(false);
  const initialFilterRef = React.useRef<string | undefined>(undefined);

  const defaultSearches = useMemo(() => [createDefaultDeliveriesSearch()], []);
  const { savedSearches, saveSearch, deleteSearch, isNameTaken } = useSavedSearches('deliveries', defaultSearches);

  const apiContext = useApiContext();

  const {
    deliveries,
    isLoading,
    currentPage,
    totalPages,
    totalSize,
    pageSize,
    hasNextPage,
    hasPreviousPage,
    fetchDeliveries,
    setPageSize,
    setFilter,
    setViewMode,
    filter,
    viewMode,
    setSort,
    sortField,
    sortDirection,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    activeSearchName,
    setActiveSearchName,
  } = useDeliveriesState();

  // Store initial filter value
  if (initialFilterRef.current === undefined) {
    initialFilterRef.current = filter;
  }

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchDeliveries();
    }
  }, [fetchDeliveries]);

  // Re-fetch deliveries when data region, profile, sort, or filter changes
  useEffect(() => {
    if (hasFetchedRef.current) {
      fetchDeliveries();
    }
  }, [apiContext.datacenter, apiContext.serviceAccount, sortField, sortDirection, filter, fetchDeliveries]);

  const handleApplyFilter = () => {
    const field = FILTER_FIELDS.find((f) => f.value === filterField);
    let newFilter = '';

    if (field?.type === 'select' && filterValue.trim()) {
      newFilter = `${filterField} = "${filterValue.trim()}"`;
    } else if (field?.type === 'date' && dateValue) {
      const formattedDate = format(dateValue, "yyyy-MM-dd'T'HH:mm:ss'Z'");
      if (dateOperator === 'between' && dateValueEnd) {
        const formattedDateEnd = format(dateValueEnd, "yyyy-MM-dd'T'HH:mm:ss'Z'");
        newFilter = `${filterField} >= "${formattedDate}" AND ${filterField} <= "${formattedDateEnd}"`;
      } else {
        newFilter = `${filterField} ${dateOperator} "${formattedDate}"`;
      }
    }

    setFilter(newFilter || undefined);
    setActiveSearchName(undefined);
  };

  const handleClearFilter = () => {
    setFilterValue('');
    setDateValue(undefined);
    setDateValueEnd(undefined);
    setFilter(undefined);
    setActiveSearchName(undefined);
  };

  const handleFieldChange = (value: string) => {
    setFilterField(value);
    setFilterValue('');
    setDateValue(undefined);
    setDateValueEnd(undefined);
  };

  const selectedField = FILTER_FIELDS.find((f) => f.value === filterField);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <PageHeader title="Deliveries" />
        <LoadingOverlay message="Loading deliveries..." />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader title="Deliveries" />
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Select value={filterField} onValueChange={handleFieldChange}>
              <SelectTrigger className="w-36" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILTER_FIELDS.map((field) => (
                  <SelectItem key={field.value} value={field.value}>
                    {field.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedField?.type === 'select' && (
              <Select value={filterValue} onValueChange={setFilterValue}>
                <SelectTrigger className="w-36" size="sm">
                  <SelectValue placeholder="Select state..." />
                </SelectTrigger>
                <SelectContent>
                  {STATE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {selectedField?.type === 'date' && (
              <>
                <Select value={dateOperator} onValueChange={setDateOperator}>
                  <SelectTrigger className="w-28" size="sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_OPERATORS.map((op) => (
                      <SelectItem key={op.value} value={op.value}>
                        {op.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Popover>
                  <Popover.Trigger asChild>
                    <button
                      type="button"
                      className="w-44 h-9.25 px-3 py-2 rounded bg-white text-sm font-normal border border-gray-300 flex flex-row items-center gap-2 hover:bg-gray-50"
                    >
                      <CalendarIcon className="h-4 w-4 shrink-0" />
                      <span>{dateValue ? format(dateValue, 'MMM d, yyyy') : 'Pick date'}</span>
                    </button>
                  </Popover.Trigger>
                  <Popover.Content variant="light" className="w-auto p-2">
                    <Calendar mode="single" selected={dateValue} onSelect={setDateValue} />
                  </Popover.Content>
                </Popover>

                {dateOperator === 'between' && (
                  <>
                    <span className="text-sm text-gray-500">and</span>
                    <Popover>
                      <Popover.Trigger asChild>
                        <button
                          type="button"
                          className="w-44 h-9.25 px-3 py-2 rounded bg-white text-sm font-normal border border-gray-300 flex flex-row items-center gap-2 hover:bg-gray-50"
                        >
                          <CalendarIcon className="h-4 w-4 shrink-0" />
                          <span>{dateValueEnd ? format(dateValueEnd, 'MMM d, yyyy') : 'Pick date'}</span>
                        </button>
                      </Popover.Trigger>
                      <Popover.Content variant="light" className="w-auto p-2">
                        <Calendar mode="single" selected={dateValueEnd} onSelect={setDateValueEnd} />
                      </Popover.Content>
                    </Popover>
                  </>
                )}
              </>
            )}

            <Button variant="secondary" size="sm" onClick={handleApplyFilter} className="rounded-md">
              Apply
            </Button>
            {filter && (
              <Button variant="tertiary" size="sm" onClick={handleClearFilter} className="rounded-md">
                Clear
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {viewMode === 'grid' && (
              <Select
                value={`${sortField}:${sortDirection}`}
                onValueChange={(value) => {
                  const [field, direction] = value.split(':') as [DeliveriesSortField, 'asc' | 'desc'];
                  setSort(field, direction);
                }}
              >
                <SelectTrigger className="w-44" size="sm">
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <ListViewSwitch viewMode={viewMode} onViewModeChange={setViewMode} />
          </div>
        </div>

        <SavedSearches
          searches={savedSearches}
          onApply={(search) => {
            setFilter(search.filter);
            if (search.sortField && search.sortDirection) {
              setSort(search.sortField as DeliveriesSortField, search.sortDirection);
            }

            setActiveSearchName(search.name);
          }}
          onDelete={deleteSearch}
          onSave={saveSearch}
          currentFilter={filter}
          currentSortField={sortField}
          currentSortDirection={sortDirection}
          isNameTaken={isNameTaken}
          activeSearchName={activeSearchName}
        />

        {filter && (
          <div className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-md">
            <span className="font-medium">Active filter:</span>{' '}
            <code className="bg-gray-200 px-1 rounded">{filter}</code>
          </div>
        )}

        {viewMode === 'list' ? <DeliveryTable /> : <DeliveryGrid deliveries={deliveries} />}

        <Pagination
          totalSize={totalSize}
          pageSize={pageSize}
          currentPage={currentPage}
          totalPages={totalPages}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onPageSizeChange={setPageSize}
          onFirst={goToFirstPage}
          onPrevious={goToPreviousPage}
          onNext={goToNextPage}
        />
      </div>
    </div>
  );
};
